import {Account} from './account.ts';
import {assert} from './assert.ts';

enum AssetTypeId {
  MagicSource = 'MAGIC_SOURCE',
  MagicSink = 'MAGIC_SINK',
  Unprotected = 'UNPROTECTED',
  Pension = 'PENSION',
  ISA = 'ISA',
  IncomeTax = 'INCOME_TAX',
  NationalInsurance = 'NATIONAL_INSURANCE ',
  EmploymentIncome = 'EMPLOYMENT_INCOME',
  Growth = 'GROWTH',
  NetIncome = 'NET_INCOME',
  StatePension = 'STATE_PENSION',
  TaxFreePensionAllowance = 'TAX_FREE_PENSION',
  AllSavings = 'ALL_SAVINGS',
}

export function serializeAssetTypeId(atid: AssetTypeId): string {
  return atid.toString();
}

// In case reading this makes you feel dirty, then you might well be a good
// person. If it makes you feel smug, then please go and do some searching
// on the internet, and then try out the various recipes people offer. If you
// are still feeling smug, then please let me know what you found. What I found
// were a lot of people who are willing to post code that doesn't even compile
// let alone work as described. Typescript folks: nice language, but it sure
// has some dirty corners
export function deserializeAssetTypeId(s: string): AssetTypeId {
  const values: AssetTypeId[] = Object.values(AssetTypeId);
  const atid = values.find(v => v.toString() === s);
  return atid!;
}

const TRACKED_ASSET_TYPES = new Set<AssetTypeId>([
  AssetTypeId.Unprotected,
  AssetTypeId.Pension,
  AssetTypeId.ISA,
  AssetTypeId.IncomeTax,
  AssetTypeId.NationalInsurance,
  AssetTypeId.NetIncome,
  AssetTypeId.TaxFreePensionAllowance,
  AssetTypeId.AllSavings,
]);

export const INCOME_SOURCE_TYPES = new Set<AssetTypeId>([
  AssetTypeId.Unprotected,
  AssetTypeId.Pension,
  AssetTypeId.ISA,
]);

// Immutable instances.
class Assets {
  static deserialize(s: string): Assets {
    const mappings = JSON.parse(s) as Array<[string, string]>;
    const aa = new Assets();

    for (const [sid, sAccount] of mappings) {
      const id = deserializeAssetTypeId(sid);
      const a = Account.deserialize(sAccount);
      aa._assets.set(id, a);
    }

    return aa;
  }

  _assets: Map<AssetTypeId, Account>;

  constructor() {
    this._assets = new Map<AssetTypeId, Account>();
  }

  serialize(): string {
    const mappings = [...this._assets.entries()]
      .filter(e => e[0] !== AssetTypeId.AllSavings)
      .map(e => [serializeAssetTypeId(e[0]), e[1].serialize()]);

    return JSON.stringify(mappings);
  }

  clone() {
    const c = new Assets();
    for (const e of this._assets.entries()) {
      c._assets.set(e[0], e[1]);
    }

    return c;
  }

  account(key: AssetTypeId): Account {
    if (key === AssetTypeId.AllSavings) {
      const savings = this.balance(AssetTypeId.ISA)
        + this.balance(AssetTypeId.Pension)
        + this.balance(AssetTypeId.Unprotected);

      return new Account(AssetTypeId.AllSavings).credit(savings);
    }

    if (!(this._assets.has(key))) {
      // auto vivify
      const key_string = key.toString();
      const acct = new Account(key_string);
      this._assets.set(key, acct);
      return acct;
    }

    return this._assets.get(key)!;
  }

  balance(key: AssetTypeId): number {
    return this.account(key).balance;
  }

  get keys(): Set<AssetTypeId> {
    return new Set(this._assets.keys()).add(AssetTypeId.AllSavings);
  }

  credit(key: AssetTypeId, amount: number): Assets {
    if (!TRACKED_ASSET_TYPES.has(key)) {
      return this;
    }

    if (key === AssetTypeId.AllSavings) {
      throw new Error('ALL SAVINGS is a "virtual" account and can only be modified by changing one of its constituents.');
    }

    const result = this.clone();
    const a1 = result.account(key);
    const a2 = a1.credit(amount);
    result._assets.set(key, a2);
    return result;
  }

  debit(key: AssetTypeId, amount: number): Assets {
    if (!TRACKED_ASSET_TYPES.has(key)) {
      return this;
    }

    if (key === AssetTypeId.AllSavings) {
      throw new Error('ALL SAVINGS is a "virtual" account and can only be modified by changing one of its constituents.');
    }

    assert(
      this._assets.has(key),
      'Cannot debit from non-existent account: ' + key.toString(),
    );
    const result = this.clone();
    const a1 = result.account(key);
    const a2 = a1.debit(amount);
    result._assets.set(key, a2);
    return result;
  }

  transfer(from: AssetTypeId, to: AssetTypeId, amount: number): Assets {
    return this.debit(from, amount).credit(to, amount);
  }

  get all_solvent(): boolean {
    return this.balance(AssetTypeId.Pension) >= 0
      && this.balance(AssetTypeId.Unprotected) >= 0
      && this.balance(AssetTypeId.ISA) >= 0;
  }

  get sum(): number {
    return [...this._assets.values()]
      .map(a => a.balance)
      .reduce((sum, number) => sum + number, 0);
  }

  get income_sources(): Assets {
    let result: Assets = new Assets();
    for (const type of INCOME_SOURCE_TYPES.values()) {
      const amount: number = this.balance(type);
      if (amount !== 0) {
        result = result.credit(type, amount);
      }
    }

    return result;
  }

  toString(): string {
    return 'Assets{'
      + [...this._assets.entries()].map(
        e => {
          const key: string = e[0];
          const account: Account = e[1];
          const amount: number = account.balance;
          return key + ':' + amount.toFixed(0);
        },
      ).join(', ') + '}';
  }

  has(key: AssetTypeId): boolean {
    return this._assets.has(key);
  }
}

export { AssetTypeId, Assets };
